import { createStore, get, set } from 'idb-keyval';
import { ulid } from 'ulid';

const MAX_OFFSET_MS = 7 * 24 * 3600 * 1000;
const kv = () => createStore('ecobox-device', 'kv');

let deviceIdPromise: Promise<string> | undefined;

/** A stable id for this installation: `dev_<ULID>`, kept in IndexedDB. */
export function getDeviceId(): Promise<string> {
  deviceIdPromise ??= (async () => {
    const store = kv();
    const existing = await get<string>('deviceId', store);
    if (typeof existing === 'string' && /^dev_[0-9A-HJKMNP-TV-Z]{26}$/.test(existing))
      return existing;
    const id = `dev_${ulid()}`;
    await set('deviceId', id, store);
    return id;
  })();
  return deviceIdPromise;
}

/** Test helper: forget the cached promise so the next call re-reads IndexedDB. */
export function resetDeviceIdCache(): void {
  deviceIdPromise = undefined;
}

export function clampOffset(ms: number): number {
  if (!Number.isFinite(ms)) return 0;
  const clamped = Math.max(-MAX_OFFSET_MS, Math.min(MAX_OFFSET_MS, Math.round(ms)));
  // Never -0 (Math.round(-0.5) is -0): Firestore writes -0 as a double, and the rules accept only an
  // integer `clockOffsetMs`, so every command would be refused until the next measurement.
  return clamped === 0 ? 0 : clamped;
}

export interface OffsetSample {
  /** Server time in epoch ms. */
  serverMs: number;
  /** Device clock when the request left and when the answer arrived. Omit both for a bare comparison. */
  sentMs?: number;
  receivedMs?: number;
}

/** Device minus server, in ms (the sign `clockOffsetMs` has in the command envelope). */
export function computeClockOffset(sample: OffsetSample, nowMs: number = Date.now()): number {
  const { serverMs, sentMs, receivedMs } = sample;
  const deviceMs =
    sentMs !== undefined && receivedMs !== undefined ? (sentMs + receivedMs) / 2 : nowMs;
  return clampOffset(deviceMs - serverMs);
}

export interface ClockOffsetTracker {
  /** Synchronous, for the command writer. */
  get(): number;
  load(): Promise<number>;
  set(ms: number): Promise<void>;
  /** Record an offset from a known server time. */
  observe(sample: OffsetSample): Promise<number>;
  /** Measure against the `Date` header of a same-origin request (one second resolution). */
  measureViaHttp(url?: string, fetchImpl?: typeof fetch): Promise<number | null>;
}

export function createClockOffsetTracker(): ClockOffsetTracker {
  let current = 0;
  const persist = async (ms: number): Promise<void> => {
    current = clampOffset(ms);
    await set('clockOffsetMs', current, kv());
  };
  const observe = async (sample: OffsetSample): Promise<number> => {
    const offset = computeClockOffset(sample);
    await persist(offset);
    return offset;
  };
  return {
    get: () => current,
    async load() {
      const saved = await get<number>('clockOffsetMs', kv());
      if (typeof saved === 'number') current = clampOffset(saved);
      return current;
    },
    set: persist,
    observe,
    async measureViaHttp(url = '/', fetchImpl = fetch) {
      const sentMs = Date.now();
      try {
        const res = await fetchImpl(url, { method: 'HEAD', cache: 'no-store' });
        const receivedMs = Date.now();
        const header = res.headers.get('date');
        const parsed = header === null ? NaN : Date.parse(header);
        if (Number.isNaN(parsed)) return null;
        // The header has whole-second resolution: the true time lies in [parsed, parsed + 1s).
        return await observe({ serverMs: parsed + 500, sentMs, receivedMs });
      } catch (err) {
        console.warn('Clock offset could not be measured.', err);
        return null;
      }
    },
  };
}
